import React from 'react'
import { Wordmark } from '@/components/Wordmark'
import { LEGAL_NAME, SUPPORT_EMAIL } from '@/lib/brand'

interface Marketing3LayoutProps {
  brand: string
  children: React.ReactNode
}

/** The ledger wordmark glyph — three stacked bars (carried from MarketingLayout). */
function LedgerGlyph() {
  return (
    <span aria-hidden="true" className="flex flex-col gap-[3px]">
      <span className="h-[3px] w-5 rounded-full bg-teal-600" />
      <span className="h-[3px] w-3.5 rounded-full bg-slate-400" />
      <span className="h-[3px] w-2 rounded-full bg-stone-300" />
    </span>
  )
}

/**
 * Light glass layout for the Landing3 variant ("daylight hotel, two rooms of night").
 * Imports NOTHING from the operator chrome and nothing from the v1/v2 marketing layouts
 * — the three variants stay fully independent. Sticky glass-light nav + editorial light
 * footer, per plan §2.11: no PMS/waitlist line, no Pricing/FAQ links.
 */
export default function Marketing3Layout({ brand, children }: Marketing3LayoutProps) {
  // Smooth anchor scrolling for this page only; restored on unmount. Honours
  // prefers-reduced-motion (static jumps) and environments without matchMedia.
  React.useEffect(() => {
    const reduced =
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduced) return
    const el = document.documentElement
    const prev = el.style.scrollBehavior
    el.style.scrollBehavior = 'smooth'
    return () => {
      el.style.scrollBehavior = prev
    }
  }, [])

  return (
    <div className="mkt3-root min-h-screen">
      <a
        href="#mkt3-main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[60] focus:rounded-lg focus:bg-teal-600 focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-white"
      >
        Skip to content
      </a>

      <header className="sticky top-0 z-50 border-b border-stone-200/70 bg-[#fafaf9]/80 backdrop-blur-md">
        <nav aria-label="Main" className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
          <a href="/#top" className="flex items-center gap-2.5" aria-label={`${brand} — home`}>
            <LedgerGlyph />
            <Wordmark brand={brand} suffix="RMS" className="mkt3-display text-[1.6rem]" />
          </a>

          <div className="hidden items-center gap-7 md:flex">
            <a href="/#how-it-works" className="text-sm text-slate-600 transition-colors hover:text-teal-700">
              How it works
            </a>
            <a href="/#control" className="text-sm text-slate-600 transition-colors hover:text-teal-700">
              Control
            </a>
            <a href="/#ai" className="text-sm text-slate-600 transition-colors hover:text-teal-700">
              AI
            </a>
            <a href="/#mobile" className="text-sm text-slate-600 transition-colors hover:text-teal-700">
              Mobile
            </a>
          </div>

          <div className="flex items-center gap-3 sm:gap-4">
            <a
              href="/users/sign_in"
              className="whitespace-nowrap text-sm font-medium text-slate-600 transition-colors hover:text-teal-700"
            >
              Sign in
            </a>
            <a
              href="/#demo"
              className="mkt3-cta inline-flex h-9 items-center whitespace-nowrap rounded-lg bg-teal-600 px-3.5 text-sm font-semibold text-white hover:bg-teal-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-2 sm:px-4"
            >
              Book a demo
            </a>
          </div>
        </nav>
      </header>

      <main id="mkt3-main">{children}</main>

      <footer className="border-t border-stone-200/80 bg-stone-100/70">
        <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6">
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <div className="flex items-center gap-2.5">
                <LedgerGlyph />
                <Wordmark brand={brand} suffix="RMS" className="mkt3-display text-xl" />
              </div>
              <p className="mt-4 max-w-[26ch] text-sm leading-relaxed text-slate-500">
                Built and run by hoteliers.
              </p>
            </div>

            <nav aria-label="Product">
              <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                Product
              </h2>
              <ul className="mt-4 space-y-2.5 text-sm">
                <li><a href="/#how-it-works" className="text-slate-600 hover:text-teal-700">How it works</a></li>
                <li><a href="/#control" className="text-slate-600 hover:text-teal-700">Control</a></li>
                <li><a href="/#targets" className="text-slate-600 hover:text-teal-700">Targets</a></li>
                <li><a href="/#ai" className="text-slate-600 hover:text-teal-700">AI</a></li>
                <li><a href="/#mobile" className="text-slate-600 hover:text-teal-700">Mobile</a></li>
              </ul>
            </nav>

            <nav aria-label="Company">
              <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                Company
              </h2>
              <ul className="mt-4 space-y-2.5 text-sm">
                <li><a href="/#who" className="text-slate-600 hover:text-teal-700">Who&rsquo;s behind it</a></li>
                <li><a href="/#demo" className="text-slate-600 hover:text-teal-700">Book a demo</a></li>
                <li>
                  <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    className="text-slate-600 hover:text-teal-700"
                  >
                    Contact — {SUPPORT_EMAIL}
                  </a>
                </li>
              </ul>
            </nav>

            <nav aria-label="Legal">
              <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                Legal
              </h2>
              <ul className="mt-4 space-y-2.5 text-sm">
                <li><a href="/terms" className="text-slate-600 hover:text-teal-700">Terms of Service</a></li>
                <li><a href="/privacy" className="text-slate-600 hover:text-teal-700">Privacy Policy</a></li>
              </ul>
            </nav>
          </div>

          <p className="mt-12 border-t border-stone-200/80 pt-6 text-sm text-slate-500">
            © 2026 {LEGAL_NAME}. Built and run by hoteliers.
          </p>
        </div>
      </footer>
    </div>
  )
}
